import dayjs from 'dayjs'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import { html, LitElement, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { when } from 'lit/directives/when.js'
import {
	concat,
	exhaustMap,
	finalize,
	fromEvent,
	map,
	merge,
	of,
	Subject,
	switchMap,
	takeUntil,
	tap,
	timer,
} from 'rxjs'
import { componentText, SchmancyFormField } from '@schmancy/mixins/index'
import { show } from '@schmancy/overlay/overlay.service'
import { inExemptLayer } from '@schmancy/overlay/overlay.stack'
import { detectDateRangeType, formatDateInstant, formatDateRange } from './date-range-helpers'
import './date-range-dialog'
import type { DateRangePreset } from './date-range-dialog'
import '@schmancy/primitives/button/button'
import '@schmancy/primitives/button/icon-button'

dayjs.extend(quarterOfYear)

function buildDefaultPresets(type: 'date' | 'datetime-local' = 'date'): DateRangePreset[] {
	const fmt = type === 'datetime-local' ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD'
	return [
		{
			id: 'today',
			label: componentText('dateRange.preset.today'),
			start: dayjs().startOf('day').format(fmt),
			end: dayjs().endOf('day').format(fmt),
		},
		{
			id: 'yesterday',
			label: componentText('dateRange.preset.yesterday'),
			start: dayjs().subtract(1, 'day').startOf('day').format(fmt),
			end: dayjs().subtract(1, 'day').endOf('day').format(fmt),
		},
		{
			id: 'last7',
			label: componentText('dateRange.preset.last7'),
			start: dayjs().subtract(6, 'day').startOf('day').format(fmt),
			end: dayjs().endOf('day').format(fmt),
		},
		{
			id: 'last30',
			label: componentText('dateRange.preset.last30'),
			start: dayjs().subtract(29, 'day').startOf('day').format(fmt),
			end: dayjs().endOf('day').format(fmt),
		},
		{
			id: 'thisWeek',
			label: componentText('dateRange.preset.thisWeek'),
			start: dayjs().startOf('week').format(fmt),
			end: dayjs().endOf('week').format(fmt),
		},
		{
			id: 'thisMonth',
			label: componentText('dateRange.preset.thisMonth'),
			start: dayjs().startOf('month').format(fmt),
			end: dayjs().endOf('month').format(fmt),
		},
		{
			id: 'lastMonth',
			label: componentText('dateRange.preset.lastMonth'),
			start: dayjs().subtract(1, 'month').startOf('month').format(fmt),
			end: dayjs().subtract(1, 'month').endOf('month').format(fmt),
		},
		{
			id: 'thisYear',
			label: componentText('dateRange.preset.thisYear'),
			start: dayjs().startOf('year').format(fmt),
			end: dayjs().endOf('year').format(fmt),
		},
	]
}

export type SchmancyDateRangeInputEvent = CustomEvent<{
	start: string | null
	end: string | null
}>

export type SchmancyDateRangeChangeEvent = CustomEvent<{
	start: string | null
	end: string | null
	preset: string | null
}>

/**
 * A date range selector composing `<schmancy-calendar mode="range">` with a
 * sticky preset chip quick-rail and a secondary typed-entry path.
 *
 * Public API: flat ISO `start`/`end`, `preset` read-back, `presets` array.
 * FormData: `${name}.start` / `${name}.end` (empty endpoint omitted).
 *
 * Every word it renders on its own behalf — preset labels, the From/To field
 * labels, the trigger and arrow aria descriptions, the live-region
 * announcement, the validation messages — resolves through the app-wide
 * wording table (`setComponentText`, `@schmancy/mixins`) under the
 * `dateRange.*` keys, so localizing it is one call at boot and nothing at the
 * call site. `presets` and `placeholder` still override per instance, and
 * `errorMessages` still overrides a single message per instance.
 *
 * `start`/`end` are naive local wall-clock strings (`YYYY-MM-DD` or
 * `YYYY-MM-DDTHH:mm`) — no timezone or offset is attached, read, or
 * reconciled. A value round-trips exactly as typed; there is no DST or
 * cross-timezone handling, by design.
 *
 * @element schmancy-date-range
 * @fires input  - Provisional sweep preview while dragging the calendar selection
 *                  { start, end }. The typed From/To fields commit directly via
 *                  `change` — they have no provisional/preview phase.
 * @fires change - Committed range change { start, end, preset }
 */
@customElement('schmancy-date-range')
export class SchmancyDateRange extends SchmancyFormField() {
	// `name`, `disabled`, `required`, `readonly`, `error`, `validationMessage`,
	// `id`, `validateOn`, `touched`, `submitted`, `markTouched`, `markSubmitted`,
	// FACE wiring, FIELD_CONNECT_EVENT dispatch — all from the mixin.

	// Delegate focus into the trigger — without this the host itself has no
	// tabindex, so `.focus()` and a `<label for="...">` click both land nowhere.
	protected static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	}

	@property() type: 'date' | 'datetime-local' = 'date'
	@property() start: string | null = null
	@property() end: string | null = null

	#presets: DateRangePreset[] | null = null

	/**
	 * The quick-rail chips. Left alone it is the built-in list, resolved on every
	 * read rather than frozen once — so the date-relative presets ("Today",
	 * "Yesterday", …) follow the clock past midnight, their format follows `type`,
	 * and their labels follow the wording table (`componentText`), which a read
	 * inside `render()` makes reactive. Assigning an array takes ownership: the
	 * getter hands back exactly that array and the built-ins never run again.
	 */
	@property({ type: Array })
	get presets(): DateRangePreset[] {
		return this.#presets ?? buildDefaultPresets(this.type)
	}
	set presets(value: DateRangePreset[]) {
		this.#presets = value
	}

	/** Selected preset id; null = custom range. Reflects to attribute. */
	@property({ reflect: true }) preset: string | null = null

	@property() min?: string
	@property() max?: string

	#placeholder: string | null = null

	/** Trigger label while no range is set. Left alone it is the
	 *  `dateRange.placeholder` wording; a value assigned here wins over it. */
	@property()
	get placeholder(): string {
		return this.#placeholder ?? componentText('dateRange.placeholder')
	}
	set placeholder(value: string) {
		this.#placeholder = value
	}

	@property() step?: 'day' | 'week' | 'month' | 'year' | number
	/** Collapses to an icon-only button on narrow viewports. */
	@property({ type: Boolean }) collapse = false
	/** Suppresses the prev/next range-navigation buttons entirely — not rendered,
	 *  not disabled, not in the a11y tree. Default false preserves existing
	 *  behavior for every current consumer. */
	@property({ type: Boolean, attribute: 'hide-nav' }) hideNav = false

	// Session subjects — ONE_PIPELINE_ONE_SUBSCRIBE in connectedCallback.
	private readonly open$ = new Subject<MouseEvent | undefined>()
	private readonly close$ = new Subject<void>()
	private readonly announce$ = new Subject<string>()

	@state() private isOpen = false
	@state() private announceMessage = ''

	// Default-capture for formResetCallback
	private startDefault: string | null = null
	private endDefault: string | null = null
	private presetDefault: string | null = null

	override disconnectedCallback(): void {
		super.disconnectedCallback()
		this.isOpen = false
	}

	override connectedCallback(): void {
		super.connectedCallback()

		// One pipeline (rxjs ONE_PIPELINE_ONE_SUBSCRIBE).
		merge(
			// Overlay session (rxjs SESSIONS_USE_HIGHER_ORDER_OBSERVABLES).
			this.open$.pipe(
				tap(() => {
					this.isOpen = true
				}),
				// Anchor comes from the tracked gesture — this session opens
				// synchronously off the trigger's click (see toggleDropdown).
				exhaustMap(() =>
					show(
						// Reactive factory — closed-over props read fresh on each mount.
						() => html`
							<schmancy-date-range-dialog
								?data-overlay-exempt=${inExemptLayer(this)}
								.type=${this.type}
								.start=${this.start}
								.end=${this.end}
								.min=${this.min}
								.max=${this.max}
								.preset=${this.preset}
								.presets=${this.presets}
								@calendar-input=${(ev: CustomEvent<{ start: string | null; end: string | null }>) => {
									ev.stopPropagation()
									this.dispatchEvent(
										new CustomEvent('input', {
											detail: { start: ev.detail.start, end: ev.detail.end },
											bubbles: true,
											composed: true,
										}),
									)
								}}
								@calendar-change=${(ev: CustomEvent<{ start: string | null; end: string | null }>) => {
									ev.stopPropagation()
									this.commitRange(ev.detail.start, ev.detail.end, null)
									// datetime-local has no time UI on the calendar — the From/To
									// time inputs below are the only way to set a time, so the
									// panel must stay open for the user to reach them. `date`
									// keeps auto-closing (nothing left to fill in).
									if (ev.detail.start && ev.detail.end && this.type !== 'datetime-local') {
										ev.target?.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }))
									}
								}}
								@preset-select=${(ev: CustomEvent<DateRangePreset>) => {
									ev.stopPropagation()
									this.commitRange(ev.detail.start, ev.detail.end, ev.detail.id)
									// Same datetime-local guard as the calendar path above — a preset
									// still needs its time reviewed/adjusted in the footer.
									if (this.type !== 'datetime-local') {
										ev.target?.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }))
									}
								}}
							></schmancy-date-range-dialog>
						`,
					).pipe(
						takeUntil(this.close$),
						finalize(() => {
							this.isOpen = false
						}),
					),
				),
			),

			this.announce$.pipe(
				switchMap(message => concat(of(message), timer(1500).pipe(map(() => '')))),
				tap(message => {
					this.announceMessage = message
				}),
			),

			fromEvent<KeyboardEvent>(document, 'keydown').pipe(tap(ev => this.handleKeyDown(ev))),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()
	}

	override willUpdate(changed: PropertyValues): void {
		super.willUpdate(changed)
		if (changed.has('start') || changed.has('end') || changed.has('name') || changed.has('disabled')) {
			if (this.name && !this.disabled) {
				const fd = new FormData()
				if (this.start) fd.append(`${this.name}.start`, this.start)
				if (this.end) fd.append(`${this.name}.end`, this.end)
				this.internals?.setFormValue(fd.has(`${this.name}.start`) || fd.has(`${this.name}.end`) ? fd : null)
			} else {
				this.internals?.setFormValue(null)
			}
			this.checkValidity()
		}
		// `this.error` keeps a DISPLAYED message re-derived on every update, which is
		// what makes it follow a `setComponentText` swap: the wording read lives inside
		// checkValidity, so an update where checkValidity doesn't run drops that
		// dependency and strands the visible message in the outgoing language.
		if (changed.has('required') || changed.has('min') || changed.has('max') || this.error) {
			this.checkValidity()
		}
	}

	override checkValidity(): boolean {
		if (this.required && (!this.start || !this.end)) {
			return this.setFieldValidity(
				{ valueMissing: true },
				this.errorMessages?.valueMissing ?? componentText('dateRange.error.incomplete'),
			)
		}
		// Compared as real instants via dayjs (not string ordering), the same
		// parser every other date comparison in this file and in the calendar
		// use, so a datetime-local value's time-of-day participates correctly.
		if (this.start && this.end && dayjs(this.end).valueOf() < dayjs(this.start).valueOf()) {
			return this.setFieldValidity(
				{ customError: true },
				this.errorMessages?.customError ?? componentText('dateRange.error.endBeforeStart'),
			)
		}
		// The bound goes into the sentence the way the trigger above it renders a
		// date — same `formatDateInstant`, same `type`, same locale — so one control
		// never shows an operator two spellings of the same instant. The raw value
		// stands in for itself only when it cannot be parsed at all, which is the
		// honest thing to show for a min/max the component could not read.
		if (this.min && this.start && dayjs(this.start).valueOf() < dayjs(this.min).valueOf()) {
			return this.setFieldValidity(
				{ rangeUnderflow: true },
				this.errorMessages?.rangeUnderflow ??
					componentText('dateRange.error.beforeMin', {
						min: formatDateInstant(this.min, this.type, this.min, this.locale),
					}),
			)
		}
		if (this.max && this.end && dayjs(this.end).valueOf() > dayjs(this.max).valueOf()) {
			return this.setFieldValidity(
				{ rangeOverflow: true },
				this.errorMessages?.rangeOverflow ??
					componentText('dateRange.error.afterMax', {
						max: formatDateInstant(this.max, this.type, this.max, this.locale),
					}),
			)
		}
		return this.setFieldValidity({})
	}

	override toFormEntries(): Array<[string, FormDataEntryValue]> {
		if (!this.name || this.disabled) return []
		const entries: Array<[string, FormDataEntryValue]> = []
		if (this.start) entries.push([`${this.name}.start`, this.start])
		if (this.end) entries.push([`${this.name}.end`, this.end])
		return entries
	}

	override get dirty(): boolean {
		return this.start !== this.startDefault || this.end !== this.endDefault
	}

	override firstUpdated(changed: PropertyValues): void {
		super.firstUpdated(changed)
		this.startDefault = this.start
		this.endDefault = this.end
		this.presetDefault = this.preset
	}

	override resetForm(): void {
		this.start = this.startDefault
		this.end = this.endDefault
		this.preset = this.presetDefault
		super.resetForm()
	}

	/**
	 * Native FACE lifecycle — restore value after bfcache / form autofill.
	 * `setFormValue` is called with a single argument elsewhere in this file
	 * (`internals?.setFormValue(fd)`), so per spec the submission value doubles
	 * as the restore state: `state` here is the same multi-entry `FormData`
	 * this component submitted, not the mixin's flat `value` string the
	 * inherited default assumes.
	 */
	override formStateRestoreCallback(state: string | File | FormData | null): void {
		if (state == null || !(state instanceof FormData) || !this.name) return
		this.start = (state.get(`${this.name}.start`) as string | null) ?? null
		this.end = (state.get(`${this.name}.end`) as string | null) ?? null
	}

	private commitRange(start: string | null, end: string | null, presetId: string | null): void {
		// The one choke point every commit runs through — calendar, preset, footer.
		// A committed range is entry finished, so the field is touched here: its own
		// blur belongs to the trigger button, which the overlay took focus from.
		this.markTouched()
		this.start = start
		this.end = end
		this.preset = presetId
		this.announce$.next(componentText('dateRange.announce', { range: this.displayLabel() }))
		this.emitChange({ start, end, preset: presetId })
	}

	private displayLabel(): string {
		if (this.preset) {
			const p = this.presets.find(x => x.id === this.preset)
			if (p) return p.label
		}
		return formatDateRange(this.start ?? '', this.end ?? '', this.type, this.placeholder, this.locale)
	}

	private toggleDropdown(e: MouseEvent) {
		e.stopPropagation()
		if (this.disabled || this.readonly) return
		if (this.isOpen) {
			this.close$.next()
		} else {
			this.open$.next(e)
		}
	}

	private shiftDateRange(direction: number, e: Event) {
		e.stopPropagation()
		if (!this.start || !this.end) return

		const fromDate = dayjs(this.start)
		const toDate = dayjs(this.end)
		if (!fromDate.isValid() || !toDate.isValid()) return

		const dateFormat = this.type === 'date' ? 'YYYY-MM-DD' : 'YYYY-MM-DDTHH:mm'
		const dir = direction > 0 ? 1 : -1
		const daysDiff = toDate.diff(fromDate, 'day') + 1

		let amount: number
		let unit: dayjs.ManipulateType

		if (this.step !== undefined) {
			if (typeof this.step === 'number') {
				amount = dir * this.step
				unit = 'day'
			} else if (this.step === 'day') {
				amount = dir * daysDiff
				unit = 'day'
			} else {
				amount = dir
				unit = this.step
			}
		} else {
			const rangeType = detectDateRangeType(fromDate, toDate)
			if (rangeType.isFullYear) {
				amount = dir
				unit = 'year'
			} else if (rangeType.isFullMonth) {
				amount = dir
				unit = 'month'
			} else if (rangeType.isFullWeek) {
				amount = dir
				unit = 'week'
			} else {
				amount = dir * daysDiff
				unit = 'day'
			}
		}

		const newFrom = fromDate.add(amount, unit)
		const newTo = toDate.add(amount, unit)

		if (this.min && newFrom.isBefore(dayjs(this.min))) return
		if (this.max && newTo.isAfter(dayjs(this.max))) return

		this.commitRange(newFrom.format(dateFormat), newTo.format(dateFormat), null)
	}

	private handleKeyDown(ev: KeyboardEvent) {
		if (!this.start || !this.end || this.disabled || this.readonly) return
		// This listener is on `document`, so `ev.target` arrives retargeted to the
		// outermost node of the composed path in the document tree — the consumer's
		// own host for a field mounted inside its shadow root, never the field. The
		// composed path still names every node the key passed through; read it here,
		// inside the handler, because it is empty once dispatch completes.
		if (!ev.composedPath().includes(this)) return

		switch (ev.key) {
			case 'PageUp':
				this.shiftDateRange(-1, ev)
				ev.preventDefault()
				break
			case 'PageDown':
				this.shiftDateRange(1, ev)
				ev.preventDefault()
				break
			case 'Home':
				if (ev.ctrlKey) {
					const fmt = this.type === 'date' ? 'YYYY-MM-DD' : 'YYYY-MM-DDTHH:mm'
					const from = dayjs(this.start)
					const to = dayjs(this.end)
					const monthStart = from.startOf('month')
					this.commitRange(monthStart.format(fmt), monthStart.add(to.diff(from, 'day'), 'day').format(fmt), null)
					ev.preventDefault()
				}
				break
			case 'End':
				if (ev.ctrlKey) {
					const fmt = this.type === 'date' ? 'YYYY-MM-DD' : 'YYYY-MM-DDTHH:mm'
					const from = dayjs(this.start)
					const to = dayjs(this.end)
					const monthEnd = to.endOf('month')
					this.commitRange(monthEnd.subtract(to.diff(from, 'day'), 'day').format(fmt), monthEnd.format(fmt), null)
					ev.preventDefault()
				}
				break
		}
	}

	private canNavigateBackward(): boolean {
		if (!this.start || !this.min) return true
		return dayjs(this.start).isAfter(dayjs(this.min))
	}

	private canNavigateForward(): boolean {
		if (!this.end || !this.max) return true
		return dayjs(this.end).isBefore(dayjs(this.max))
	}

	render() {
		const showError = this.error && !!this.validationMessage
		// The collapsed trigger is a circular <schmancy-icon-button>, whose own
		// :host radius an outer-tree class outranks — the ring rung has to be the
		// same shape or the outline squares off a circle.
		const errorRing = showError ? ' rounded-full outline-solid outline-2 outline-error-default' : ''
		const label = this.displayLabel()
		const triggerLabel = componentText('dateRange.trigger', { range: label })
		// The overlay this trigger opens never resolves to role="dialog" — it's
		// an anchored popover (role="region") except on narrow viewports, where
		// it's forced to a sheet (role="dialog"). "true" (generic popup) is the
		// one aria-haspopup token honest about both.
		const hasPopup = 'true'
		// No `aria-readonly` on either trigger: ARIA gives the `button` role no
		// read-only state (axe reports `aria-allowed-attr` against it), and the
		// custom-element host that would otherwise carry it is roleless, so the
		// attribute would reach nothing. `readonly` travels as :state(readonly),
		// both triggers' own `readonly` state, and the disabled nav arrows.
		const describedBy = this.hint || showError ? `hint-${this.id}` : undefined

		return html`
			<div class="relative ${this.disabled ? 'opacity-60 pointer-events-none' : ''}">
				<div class="sr-only" role="status" aria-live="polite" aria-atomic="true">${this.announceMessage}</div>

				<schmancy-icon-button
					class="${this.collapse ? 'lg:hidden' : 'hidden'}${errorRing}"
					variant="outlined"
					type="button"
					aria-haspopup=${hasPopup}
					aria-expanded=${this.isOpen}
					aria-invalid=${showError ? 'true' : 'false'}
					aria-describedby=${ifDefined(describedBy)}
					aria-label=${triggerLabel}
					@click=${(e: MouseEvent) => this.toggleDropdown(e)}
					?disabled=${this.disabled}
					?readonly=${this.readonly}
				>
					date_range
				</schmancy-icon-button>

				<!-- Full trigger row: [← | range label | →], or just the label alone
				(single track) when hideNav suppresses navigation. Grid, not flex —
				schmancy-button's :host has an unconditional flex-shrink:0, so only a
				grid item's minmax(0,1fr) track lets the label shrink. The nav
				schmancy-icon-buttons carry an explicit justify-self (start/end): a
				grid item's own default is justify-self:stretch, which is independent
				of their :host's flex-driven INLINE_FLEX_HOST_NO_SHRINK sizing and
				otherwise stretches them to fill their auto track. -->
				<section
					@click=${(ev: Event) => ev.stopPropagation()}
					aria-invalid=${showError ? 'true' : 'false'}
					class=${this.classMap({
						'w-fit max-w-full items-center rounded-xl border-1 border-solid border-control-border': true,
						grid: !this.collapse,
						'hidden lg:grid': this.collapse,
						'grid-cols-[auto_minmax(0,1fr)_auto]': !this.hideNav,
						'grid-cols-1': this.hideNav,
						'outline-solid outline-2 outline-error-default': showError,
					})}
				>
					${when(
						!this.hideNav,
						() => html`
							<schmancy-icon-button
								class="justify-self-start"
								type="button"
								aria-label=${componentText('dateRange.previous', { range: label })}
								@click=${(e: Event) => this.shiftDateRange(-1, e)}
								?disabled=${this.disabled || this.readonly || !this.start || !this.end || !this.canNavigateBackward()}
							>
								arrow_left
							</schmancy-icon-button>
						`,
					)}

					<schmancy-button
						class="w-fit max-w-full"
						variant="text"
						type="button"
						aria-haspopup=${hasPopup}
						aria-expanded=${this.isOpen}
						aria-invalid=${showError ? 'true' : 'false'}
						aria-describedby=${ifDefined(describedBy)}
						aria-label=${triggerLabel}
						@click=${(e: MouseEvent) => this.toggleDropdown(e)}
						?disabled=${this.disabled}
						?readonly=${this.readonly}
					>
						<span class="block min-w-0 truncate">${label}</span>
					</schmancy-button>

					${when(
						!this.hideNav,
						() => html`
							<schmancy-icon-button
								class="justify-self-end"
								type="button"
								aria-label=${componentText('dateRange.next', { range: label })}
								@click=${(e: Event) => this.shiftDateRange(1, e)}
								?disabled=${this.disabled || this.readonly || !this.start || !this.end || !this.canNavigateForward()}
							>
								arrow_right
							</schmancy-icon-button>
						`,
					)}
				</section>

				${when(
					this.hint || showError,
					() => html`
						<div
							id="hint-${this.id}"
							role=${ifDefined(showError ? 'alert' : undefined)}
							class=${this.classMap({ 'mt-1 text-sm wrap-anywhere': true, 'text-error-default': showError, 'text-surface-onVariant': !showError })}
						>
							${showError ? this.validationMessage : this.hint}
						</div>
					`,
				)}
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-date-range': SchmancyDateRange
	}
}
